import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Pagination,
  Paper,
  Typography,
} from '@mui/material';
import PageLayout from '../../components/layout/PageLayout';
import PageHeader from '../../components/layout/PageHeader';
import DirectoryFilters from '../../components/directory/DirectoryFilters';
import OfferCard from '../../components/directory/OfferCard';
import ProviderCard from '../../components/directory/ProviderCard';
import { Loading, ProviderGridSkeleton, StackSkeleton, PostCardSkeleton } from '../../components/layout/Skeletons';
import { useOffers } from '../../hooks/directory/useOffers';
import { useDirectory } from '../../hooks/directory/useDirectory';

/**
 * The shops of the quarter, and what they are offering.
 *
 * Two blocks, in the order somebody uses them: what is on this week, then
 * where the shops are. The offers come first because they are the only part
 * that changes — a resident who came back to the page came back for those.
 *
 * Shops are here rather than in the directory because the errand is not the
 * same: choosing a plumber is choosing whom to call, choosing a bakery is
 * choosing where to walk.
 */
function Commerces() {
  const {
    filters,
    categoryOptions,
    neighborhoods,
    providers,
    meta,
    city,
    loading,
    error,
    setFilter,
    resetFilters,
  } = useDirectory({ kind: 'COMMERCE' });

  const { offers, loading: loadingOffers, error: offersError, reload: reloadOffers } = useOffers({
    neighborhood: filters.neighborhood,
    category: filters.category,
  });

  const total = meta?.total_items || 0;

  return (
    <PageLayout centered={false}>
      <Box sx={{ width: '100%' }}>
        <PageHeader
          reserveLede
          title="Les commerces"
          lede={
            city
              ? `Les boutiques de ${city.name}, leurs horaires, et ce qu’elles proposent en ce moment.`
              : 'Les boutiques du quartier, leurs horaires, et ce qu’elles proposent en ce moment.'
          }
        />

        {/* The same form as the services page, deliberately: the two list
            different things and narrow them the same way. */}
        <DirectoryFilters
          filters={filters}
          categoryOptions={categoryOptions}
          neighborhoods={neighborhoods}
          onChange={setFilter}
          onReset={resetFilters}
          searchLabel="Un nom ou un commerce"
          searchPlaceholder="boulangerie, fleuriste, primeur…"
          categoryLabel="Types de commerce"
          allCategoriesLabel="Tous"
        />

        {/* What is on, and what is announced: the only part of the page that
            changes. An offer for next Saturday is worth reading today — that
            is why it was written on the Monday. */}
        <Box component="section" sx={{ mb: 5 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 2.5 }}>
            Les offres du moment
          </Typography>

          {offersError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {offersError}
            </Alert>
          )}

          {loadingOffers ? (
            <Loading label="Chargement des offres">
              <StackSkeleton count={2}>{PostCardSkeleton}</StackSkeleton>
            </Loading>
          ) : offers.length > 0 ? (
            /* The same grid as the shops below — three across from md, the
               same gutter — so a card is a card whichever block it is in.
               `minmax(0, …)` rather than a bare `1fr`: a column sized on its
               contents is a column a long word can widen at its neighbours'
               expense. */
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  md: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 3,
              }}
            >
              {offers.map((offer) => (
                <OfferCard key={offer.uuid} offer={offer} citySlug={city?.slug} onModerated={reloadOffers} />
              ))}
            </Box>
          ) : (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="body1">
                Aucune offre annoncée pour l’instant.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* And where the shops are, which changes far less often. */}
        <Box component="section">
          <Typography variant="h3" component="h2" sx={{ mb: 2.5 }}>
            {total > 0 ? `${total} commerce${1 === total ? '' : 's'}` : 'Les commerces'}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Loading label="Chargement des commerces">
              <ProviderGridSkeleton count={3} />
            </Loading>
          ) : providers.length > 0 ? (
            <Grid container spacing={3}>
              {providers.map((provider) => (
                <Grid item xs={12} sm={6} md={4} key={provider.uuid} sx={{ minWidth: 0 }}>
                  <ProviderCard provider={provider} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed' }}>
              <Typography variant="h4" component="p" sx={{ mb: 1 }}>
                Aucun commerce ici
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Essayez un autre quartier, ou effacez les filtres. Vous tenez un commerce ?
                Inscrivez-le, c’est gratuit.
              </Typography>
              <Button component={RouterLink} to="/pro/bienvenue" variant="contained">
                Inscrire mon commerce
              </Button>
            </Paper>
          )}

          {meta && meta.total_pages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
              <Pagination
                count={meta.total_pages}
                page={meta.current_page}
                onChange={(event, page) => setFilter('page', 1 === page ? '' : String(page))}
              />
            </Box>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}

export default Commerces;
